import * as React from 'react';
import { Search } from '@weddingspot/ws-api-client';
import { Checkbox } from '@cvent/carina/components/Checkbox';
import { RadioGroup } from '@cvent/carina/components/RadioGroup';

import FilterInterstitial from './FilterInterstitial';
import { LocationsFilters } from './LocationsFilters';
import { StylesFilters } from './StylesFilters';
import { BudgetRangeFilter } from './BudgetRangeFilter';
import { GuestCountFilter } from './GuestCountFilter';
import { LocationSearchFilter } from './LocationSearchFilter';
import { FormikContextType, useFormikContext } from 'formik';
import { SRPFormikValues } from '.';
import { getCanonicalUrl } from './utils';
import { useSrpVariant } from './hooks';
import { BudgetRangePopover } from './BudgetRangePopover';
import { resetAllFilter, filterDefaultValuesGroup } from './FiltersSidebar';
import { AnalyticsEvent, logEvent } from '@weddingspot/ws-analytics';
import { FilterTypes } from './FilterBar';

const _styles = require('./MwebFilters.scss');

interface MwebFiltersProps {
    visibleFilters: FilterTypes | null;
    filtersChoices: Search.SearchFilters;
    selectedMarket: string;
    onDismiss: () => void;
    isSmallScreen?: boolean;
    marketLinks?: Search.RelativeUrl[];
}

const MwebFitlers = (props: MwebFiltersProps) => {
    const { visibleFilters, filtersChoices, onDismiss, isSmallScreen } = props;
    const formikContext = useFormikContext<SRPFormikValues>();
    const { values, setFieldValue, setFieldTouched, handleChange } = formikContext;
    const { locations, styles, budget, guests, services, byo } = filtersChoices;
    const selectedMarket = props.selectedMarket;
    const variant = useSrpVariant();

    const clearLocationFilter = (
        formikContext: FormikContextType<SRPFormikValues>,
        subgroups: Array<keyof typeof filterDefaultValuesGroup> = ['location', 'styles', 'other']
    ) => {
        logEvent(AnalyticsEvent.EVTNM_CLICKED_RESET_FILTERS, { Type: 'Regions', 'SRP Version': 2 });
        resetAllFilter(formikContext, subgroups);
    };

    const clearStylesFilter = (
        formikContext: FormikContextType<SRPFormikValues>,
        subgroups: Array<keyof typeof filterDefaultValuesGroup> = ['location', 'styles', 'other']
    ) => {
        logEvent(AnalyticsEvent.EVTNM_CLICKED_RESET_FILTERS, { Type: 'Styles', 'SRP Version': 2 });
        resetAllFilter(formikContext, subgroups);
    };

    const outdoorStyleValue = styles?.options.find((e) => e.label === 'Outdoor')!.value;

    React.useEffect(() => {
        if (visibleFilters === 'Outdoor' && formikContext.values.styles && outdoorStyleValue) {
            setFieldValue('styles', {
                ...formikContext.values.styles,
                [outdoorStyleValue]: !formikContext.values.styles[outdoorStyleValue],
            });
            onDismiss();
        }
    }, [visibleFilters]);

    return (
        <div className={_styles.container}>
            <FilterInterstitial
                visible={visibleFilters === 'Search'}
                title='Search'
                close={onDismiss}
                bodyClassName={_styles.searchFiltersBody}
                hideSecondaryDismiss={true}
            >
                {/*
                    NOTE: We don't render the location filter when it's hidden because
                    we don't use it as part of a form and we want to leverage component
                    lifecycle events to handle autofocus and resetting the component
                 */}
                {visibleFilters === 'Search' && (
                    <LocationSearchFilter
                        marketLinks={props.marketLinks}
                        onMarketSelected={(market) => {
                            window.location.href = getCanonicalUrl({ market: market.market, variant: variant || undefined });
                        }}
                        onSearchResultClick={onDismiss}
                    />
                )}
            </FilterInterstitial>

            {locations && (
                <FilterInterstitial
                    visible={visibleFilters === 'Locations'}
                    title='Locations'
                    close={onDismiss}
                    hideSecondaryDismiss={!isSmallScreen}
                    clear={() => clearLocationFilter(formikContext, ['location'])}
                >
                    <LocationsFilters locations={locations} collapsibleSubsections={false} />
                </FilterInterstitial>
            )}
            {styles && (
                <FilterInterstitial
                    visible={visibleFilters === 'Venue Style'}
                    title='Venue'
                    close={onDismiss}
                    hideSecondaryDismiss={!isSmallScreen}
                    clear={() => clearStylesFilter(formikContext, ['styles'])}
                >
                    <StylesFilters styles={styles} collapsibleSubsections={false} />
                </FilterInterstitial>
            )}
            {(budget || guests || services || byo) && (
                <FilterInterstitial
                    visible={visibleFilters === 'More Filters'}
                    title='More Filters'
                    close={onDismiss}
                    bodyClassName={_styles.moreFiltersBody}
                    clear={() => resetAllFilter(formikContext, ['other'])}
                >
                    {budget && (
                        <div className={_styles.moreFiltersSection}>
                            <div className={_styles.moreFiltersSectionTitle}>
                                <div>Budget</div>
                                <div className={_styles.budgetRangePopover}>
                                    <BudgetRangePopover mobileInteraction={true} />
                                </div>
                            </div>
                            <BudgetRangeFilter
                                isVisible={visibleFilters === 'More Filters'}
                                min={budget.min}
                                max={budget.max}
                                defaultValue={budget.default}
                                onChange={(values) => {
                                    setFieldValue('budget', values);
                                    setFieldTouched('budget', true);
                                }}
                            />
                        </div>
                    )}

                    {guests && (
                        <div className={_styles.moreFiltersSection}>
                            <div className={_styles.moreFiltersSectionTitle}>Guests</div>
                            <GuestCountFilter
                                isVisible={visibleFilters === 'More Filters'}
                                min={guests.min}
                                max={guests.max}
                                defaultValue={guests.default}
                                onChange={(values) => {
                                    setFieldValue('guestCount', values);
                                    setFieldTouched('guestCount', true);
                                }}
                            />
                        </div>
                    )}

                    {services && (
                        <div className={_styles.moreFiltersSection}>
                            <div className={_styles.moreFiltersSectionTitle}>Services</div>
                            <RadioGroup
                                name='sr'
                                options={services.options}
                                selected={values.sr}
                                onUpdate={(value: string, radio: any) => {
                                    setTimeout(() => {
                                        setFieldValue('sr', value);
                                        setFieldTouched('sr', true);
                                        logEvent(AnalyticsEvent.EVTNM_CLICKED_FILTER, {
                                            'Current Market': selectedMarket,
                                            Type: 'Services',
                                            Label: radio.label,
                                            Value: value,
                                            Checked: values.sr,
                                            'SRP Version': 2,
                                        });
                                    });
                                }}
                            />
                            {services.links && (
                                <div className={_styles.servicesLinks}>
                                    {services.links.map((l, idx) => (
                                        <a href={l.url} key={idx}>
                                            {l.label}
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {byo && (
                        <div className={_styles.moreFiltersSection}>
                            <div className={_styles.moreFiltersSectionTitle}>Alcohol &amp; Beverages</div>
                            <Checkbox name='byo' id='byo' checked={values.byo} onChange={handleChange}>
                                Allows BYO
                            </Checkbox>
                        </div>
                    )}
                </FilterInterstitial>
            )}

            {/* Desktop  map part  */}

            {budget && (
                <FilterInterstitial
                    visible={visibleFilters === 'Budget'}
                    title='Budget'
                    close={onDismiss}
                    hideSecondaryDismiss={true}
                    bodyClassName={_styles.moreFiltersBody}
                    clear={() => resetAllFilter(formikContext, ['budget'])}
                >
                    <div className={_styles.moreFiltersSection}>
                        <div className={_styles.moreFiltersSectionTitle}>
                            <div>Budget</div>
                            <div className={_styles.budgetRangePopover}>
                                <BudgetRangePopover mobileInteraction={true} />
                            </div>
                        </div>
                        <BudgetRangeFilter
                            isVisible={visibleFilters === 'Budget'}
                            min={budget.min}
                            max={budget.max}
                            defaultValue={budget.default}
                            onChange={(values) => {
                                setFieldValue('budget', values);
                                setFieldTouched('budget', true);
                            }}
                        />
                    </div>
                </FilterInterstitial>
            )}

            {guests && (
                <FilterInterstitial
                    visible={visibleFilters === 'Guests'}
                    title='Guests'
                    close={onDismiss}
                    hideSecondaryDismiss={true}
                    bodyClassName={_styles.moreFiltersBody}
                    clear={() => resetAllFilter(formikContext, ['guests'])}
                >
                    <div className={_styles.moreFiltersSection}>
                        <div className={_styles.moreFiltersSectionTitle}>Guests</div>
                        <GuestCountFilter
                            isVisible={visibleFilters === 'Guests'}
                            min={guests.min}
                            max={guests.max}
                            defaultValue={guests.default}
                            onChange={(values) => {
                                setFieldValue('guestCount', values);
                                setFieldTouched('guestCount', true);
                            }}
                        />
                    </div>
                </FilterInterstitial>
            )}

            {services && (
                <FilterInterstitial
                    visible={visibleFilters === 'Services'}
                    title='Services'
                    close={onDismiss}
                    hideSecondaryDismiss={true}
                    bodyClassName={_styles.moreFiltersBody}
                    clear={() => resetAllFilter(formikContext, ['services'])}
                >
                    <div className={_styles.moreFiltersSection}>
                        <div className={_styles.moreFiltersSectionTitle}>Services</div>
                        <RadioGroup
                            name='sr'
                            options={services.options}
                            selected={values.sr}
                            onUpdate={(value: string, radio: any) => {
                                setTimeout(() => {
                                    setFieldValue('sr', value);
                                    setFieldTouched('sr', true);
                                    logEvent(AnalyticsEvent.EVTNM_CLICKED_FILTER, {
                                        'Current Market': selectedMarket,
                                        Type: 'Services',
                                        Label: radio.label,
                                        Value: value,
                                        Checked: values.sr,
                                        'SRP Version': 2,
                                    });
                                });
                            }}
                        />
                        {services.links && (
                            <div className={_styles.servicesLinks}>
                                {services.links.map((l, idx) => (
                                    <a href={l.url} key={idx}>
                                        {l.label}
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>
                </FilterInterstitial>
            )}

            {byo && (
                <FilterInterstitial
                    visible={visibleFilters === 'Alcohol & Beverages'}
                    title='Alcohol & Beverages'
                    close={onDismiss}
                    hideSecondaryDismiss={true}
                    bodyClassName={_styles.moreFiltersBody}
                    clear={() => resetAllFilter(formikContext, ['byo'])}
                >
                    <div className={_styles.moreFiltersSection}>
                        <div className={_styles.moreFiltersSectionTitle}>Alcohol &amp; Beverages</div>
                        <Checkbox name='byo' id='byo' checked={values.byo} onChange={handleChange}>
                            Allows BYO
                        </Checkbox>
                    </div>
                </FilterInterstitial>
            )}
        </div>
    );
};

export default MwebFitlers;
